import { useQuery, useQueryClient } from "@tanstack/react-query"

import {
  MessageChannel,
  UserMessageChannel,
  V1PaginatedResponse,
} from "@bullseye/types"

import { useHTTPContext } from "../providers/HTTPProvider"
import { useUserContext } from "../providers/UserAuthProvider/UserProvider"
import { useGetRequest, usePostRequest } from "./useRequest"

export type UserMessageChannelData = UserMessageChannel & {
  message_channel: MessageChannel
  is_verified: boolean
  slug: string
}

type UserMessageChannelResponse = V1PaginatedResponse<UserMessageChannelData>

export const useUserMessageChannel = (id: string | undefined | null) => {
  const { user, accessToken } = useUserContext()

  const { data, ...rest } = useGetRequest<UserMessageChannelResponse>(
    {
      method: "GET",
      path: `v1/message-channels/${id}/users`,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        user_id: user.id,
      },
    },
    {
      enabled: !!id && !!accessToken,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  )

  return {
    userMessageChannel: data?.data?.[0],
    ...rest,
  }
}

type MessageChannelDetailResponse = {
  data: MessageChannel[]
}

export const useUserMessageChannels = (ids: string[] | null | undefined) => {
  const { user, accessToken } = useUserContext()
  const { apiBase } = useHTTPContext()
  const queryClient = useQueryClient()

  const safeIds = ids ?? []
  const idsKey = [...safeIds].sort().join(",")

  const { data, isLoading, error, refetch, isRefetching } = useQuery(
    ["message-channels-batch", idsKey],
    async () => {
      if (safeIds.length === 0) return []

      const results = await Promise.all(
        safeIds.map(async (id) => {
          const cached = queryClient.getQueryData<UserMessageChannelData>([
            "message-channel",
            id,
          ])
          if (cached) return cached

          try {
            const [usersRes, detailsRes] = await Promise.all([
              fetch(
                `${apiBase}/v1/message-channels/${id}/users?user_id=${user.id}`,
                {
                  method: "GET",
                  headers: { Authorization: `Bearer ${accessToken}` },
                },
              ),
              fetch(`${apiBase}/v1/message-channels/${id}`, {
                method: "GET",
                headers: { Authorization: `Bearer ${accessToken}` },
              }),
            ])

            if (usersRes.status === 401 || detailsRes.status === 401) {
              console.warn(`No access to message channel ${id}`)
              return undefined
            }

            if (!usersRes.ok) {
              console.warn(`Failed to fetch message channel users ${id}`)
              return undefined
            }

            const usersJson =
              (await usersRes.json()) as UserMessageChannelResponse
            const channelData = usersJson.data?.[0]

            if (channelData) {
              queryClient.setQueryData(["message-channel", id], channelData)
            }

            if (detailsRes.ok) {
              const detailsJson =
                (await detailsRes.json()) as MessageChannelDetailResponse
              const messageChannelDetails = detailsJson.data?.[0]
              if (messageChannelDetails) {
                queryClient.setQueryData(
                  ["message-channel-by-stream-id", id],
                  messageChannelDetails,
                )
              }
            }

            return channelData
          } catch (err) {
            console.warn(`Error fetching message channel ${id}:`, err)
            return undefined
          }
        }),
      )

      return results
    },
    {
      enabled: !!accessToken && safeIds.length > 0,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: false,
    },
  )

  return {
    messageChannels: data ?? [],
    isLoading,
    error,
    refetch,
    isRefetching,
  }
}

export const useAcceptChat = (messageChannel: MessageChannel) => {
  const queryClient = useQueryClient()
  const { userMessageChannel, refetch, key } = useUserMessageChannel(
    messageChannel.id,
  )
  const { accessToken } = useUserContext()

  async function handleMutate() {
    await queryClient.cancelQueries(key)

    const updatedUserMessageChannel = {
      ...userMessageChannel,
      is_accepted: true,
    }

    queryClient.setQueryData(key, { data: { data: [updatedUserMessageChannel] }, status: 200 })
  }

  const { postRequestAsync, isLoading, error } = usePostRequest<null, null>({
    path: `v1/chat/message-channels/${messageChannel.id}/accept`,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    onMutate: async () => handleMutate(),
  })

  const accept = async () => {
    await postRequestAsync(null, {
      onSuccess: () => {
        return handleMutate()
      },
      onError: async (err) => {
        await refetch()
        throw err
      },
    })
  }

  return {
    accept,
    isLoading,
    error,
  }
}